/**
 * processTreeHydration.js
 * The process Tree surface's OWN subworkflow hydrator (story 118, RN35-RN38, D33).
 *
 * Why not reuse `cascadeApproval.js#hydrateWorkflowTree`: it resolves with `Promise.all`, so ONE rejected
 * subworkflow rejects the whole tree (RN36.1, risk R3) — exactly what D33 forbids. Here each subworkflow
 * is fetched inside its own try/catch: the ones that load are used, the ones that fail are reported by
 * node id, and the view is drawn either way (POL1: the tree never goes blank).
 *
 * PO review 2026-09-22 — dos cambios de rendimiento, ninguno de comportamiento:
 *
 *  1. Los hermanos de un nivel se piden A LA VEZ, con tope de concurrencia. Antes era un `for … await`
 *     estricto: ASCOD_LETONIA son 27 subworkflows en profundidad 2, o sea 27 idas y vueltas en fila
 *     india cuando 25 de ellas podian viajar juntas. La tolerancia a fallos NO cambia — cada peticion
 *     sigue teniendo su try/catch, que es lo que D33 pedia y lo que `Promise.all` rompia.
 *  2. Cada subworkflow se AVISA en cuanto llega (`onLoaded`), en vez de publicar los 27 al final. La
 *     vista los va pintando segun caen; antes el arbol se quedaba con guiones en las cifras hasta que
 *     terminaba el ultimo, sin ninguna senal de que estuviera pasando algo.
 *
 * Dos peticiones al MISMO subworkflow ya no se duplican: mientras una esta en vuelo, la siguiente espera
 * su promesa. En serie esto lo resolvia el orden; en paralelo hay que decirlo.
 *
 * Why it returns instead of writing: the save diff compares `originalData` against `workflowData.workflow`
 * excluding only `workflowPositions`/`workflow` (`Workflow.jsx:620-637`), so writing the loaded operations
 * into the reducer model would travel in the `PUT` as a user change (RN7/RN37, risk R4). The hydrated tree
 * and the `subWorkflowsById` cache live in the view's local state, and `applyHydratedSubWorkflows` overlays
 * that cache on the LIVE model on every render, so unsaved edits of the first level stay visible (S11).
 *
 * The fetcher is injected, like `cascadeWorkflowLoader.js`, so the walk is testable with a double.
 *
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Node} Node
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} Workflow
 */
import { apiGet } from '../../generic/Api_Functions'
import { getRandomUUID } from '../../generic/utilities/randomuuid'
import { clearTempDocumentScopes } from './tempDocumentScopes'
import { failureMessageOf } from './libraryFailure'

/**
 * Cuantas peticiones de subworkflow viajan a la vez. Seis porque es lo que un navegador abre por host en
 * HTTP/1.1: subirlo no acelera, solo hace cola en otro sitio, y deja al resto de la pantalla sin hueco
 * para sus propias peticiones.
 */
const MAX_PARALLEL_SUBWORKFLOWS = 6

/**
 * `list.map(task)` con tope de tareas simultaneas, conservando el ORDEN del resultado — el arbol lee los
 * nodos en el orden del modelo y reordenarlos cambiaria el dibujo.
 */
const mapWithLimit = async (list, limit, task) => {
  const results = new Array(list.length)
  let next = 0
  const worker = async () => {
    while (next < list.length) {
      const index = next
      next += 1
      results[index] = await task(list[index])
    }
  }
  await Promise.all(new Array(Math.min(limit, list.length)).fill(null).map(worker))
  return results
}

const isSetNode = (node) => node?.nodeType === 'WORKFLOW'
const isHydrated = (node) => isSetNode(node) && Array.isArray(node.subWorkflow?.nodes)

/**
 * RN35: the WORKFLOW nodes, at any depth of what is already loaded, whose subworkflow arrived without its
 * operations and can therefore be fetched (`WorkflowFlow.jsx:201-214`). A set with no subworkflow id
 * cannot be loaded and is left out.
 * @param {Node[]} nodes
 * @returns {{ nodeId: string, subWorkflowId: * }[]}
 */
const collectUnhydratedSets = (nodes) => {
  const pending = []
  const visit = (level) => {
    (level || []).forEach((node) => {
      if (!isSetNode(node)) {
        return
      }
      if (isHydrated(node)) {
        visit(node.subWorkflow.nodes)
        return
      }
      if (node.subWorkflow?.id) {
        pending.push({ nodeId: node.id, subWorkflowId: node.subWorkflow.id })
      }
    })
  }
  visit(nodes)
  return pending
}

/**
 * RN35/RN36/RN38: loads every not-yet-loaded subworkflow, recursively, ONE BY ONE and fault-tolerantly.
 * Returns a new tree (the input is never mutated), the node ids whose load failed — which the projection
 * marks and the footer warns about (RN36/RN71) — and every subworkflow that did load, by id, so a second
 * entry into the surface reuses them instead of asking again (RN38).
 *
 * @param {Workflow} workflow The live editor model (read only)
 * @param {function(*): Promise<Workflow>} fetchWorkflowById Fetches a full workflow by id
 * @param {Object<string, Workflow>} [knownSubWorkflowsById] Already loaded subworkflows, reused as-is
 * @returns {Promise<{ workflow: Workflow, failedSetNodeIds: string[], subWorkflowsById: Object<string, Workflow> }>}
 */
const hydrateProcessTree = async (workflow, fetchWorkflowById, knownSubWorkflowsById = {}, onLoaded = null) => {
  const failedSetNodeIds = []
  // Story 167 (E-16, D16.5): the server's message of each failed set, so the mini tree can say it.
  const failedMessageByNodeId = {}
  const subWorkflowsById = { ...knownSubWorkflowsById }
  // Las peticiones en vuelo, por id: dos conjuntos que apunten al mismo subworkflow comparten la misma.
  const inFlight = new Map()

  const hydrateLevel = async (nodes) => {
    if (!Array.isArray(nodes)) {
      return nodes
    }
    return mapWithLimit(nodes, MAX_PARALLEL_SUBWORKFLOWS, hydrateNode)
  }

  const loadSubWorkflow = async (subWorkflowId) => {
    if (subWorkflowsById[subWorkflowId]) {
      return subWorkflowsById[subWorkflowId]
    }
    if (!inFlight.has(subWorkflowId)) {
      inFlight.set(subWorkflowId, fetchWorkflowById(subWorkflowId))
    }
    const loaded = await inFlight.get(subWorkflowId)
    subWorkflowsById[subWorkflowId] = loaded
    return loaded
  }

  const hydrateNode = async (node) => {
    if (!isSetNode(node)) {
      return node
    }
    if (isHydrated(node)) {
      return { ...node, subWorkflow: { ...node.subWorkflow, nodes: await hydrateLevel(node.subWorkflow.nodes) } }
    }
    const subWorkflowId = node.subWorkflow?.id
    if (!subWorkflowId) {
      return node
    }
    let loaded
    try {
      loaded = await loadSubWorkflow(subWorkflowId)
    } catch (error) {
      // D33/RN36: one subworkflow that fails is one marked set, never a broken tree.
      failedSetNodeIds.push(node.id)
      failedMessageByNodeId[node.id] = failureMessageOf(error)
      if (onLoaded) {
        onLoaded({ failedSetNodeId: node.id, message: failedMessageByNodeId[node.id] })
      }
      return node
    }
    // Se avisa antes de bajar un nivel: asi la vista pinta este conjunto mientras se piden sus hijos.
    if (onLoaded) {
      onLoaded({ subWorkflowId, subWorkflow: loaded })
    }
    return { ...node, subWorkflow: { ...node.subWorkflow, ...loaded, nodes: await hydrateLevel(loaded.nodes) } }
  }

  return {
    workflow: { ...workflow, nodes: await hydrateLevel(workflow?.nodes) },
    failedSetNodeIds,
    failedMessageByNodeId,
    subWorkflowsById
  }
}

/**
 * RN37: overlays the loaded subworkflows on the LIVE model without touching it, so the lens reads the
 * model the user is editing (unsaved changes included, RN6/S11) with the sets filled in from local state.
 * A set already loaded in the model wins over the cache: what the editor holds is newer.
 * @param {Workflow} workflow
 * @param {Object<string, Workflow>} subWorkflowsById
 * @param {function(string, Workflow): Workflow} [contentOf] Pure transform of a loaded subworkflow, given the id of the node it hangs from (default: as loaded)
 * @returns {Workflow}
 */
const applyHydratedSubWorkflows = (workflow, subWorkflowsById, contentOf = (nodeId, loaded) => loaded) => {
  if (!subWorkflowsById || Object.keys(subWorkflowsById).length === 0) {
    return workflow
  }
  const overlayLevel = (nodes) => (nodes || []).map((node) => {
    if (!isSetNode(node)) {
      return node
    }
    if (isHydrated(node)) {
      return { ...node, subWorkflow: { ...node.subWorkflow, nodes: overlayLevel(node.subWorkflow.nodes) } }
    }
    const cached = node.subWorkflow?.id ? subWorkflowsById[node.subWorkflow.id] : null
    if (!cached) {
      return node
    }
    const loaded = contentOf(node.id, cached)
    return { ...node, subWorkflow: { ...node.subWorkflow, ...loaded, nodes: overlayLevel(loaded.nodes) } }
  })
  return { ...workflow, nodes: overlayLevel(workflow?.nodes) }
}

/**
 * The real fetcher of the surface: same bookkeeping `handleSubWorkflowDoubleClick` (`Workflow.jsx:791-794`)
 * and `cascadeWorkflowLoader.js:16-21` apply to a lazily loaded subworkflow, so what the tree loads can be
 * reused by the editor without dragging a stale temp document scope along.
 * @param {*} workflowId
 * @returns {Promise<Workflow>}
 */
const fetchWorkflowForProcessTree = async (workflowId) => {
  const response = await apiGet('workflow/' + workflowId)
  response.tempWorkflowUUID = getRandomUUID()
  clearTempDocumentScopes(response)
  return response
}

export {
  collectUnhydratedSets,
  hydrateProcessTree,
  applyHydratedSubWorkflows,
  fetchWorkflowForProcessTree
}
